import type { ReportConfig } from '@/types/custom-reports';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

interface Step2GroupingProps {
  config: ReportConfig;
  updateConfig: (updates: Partial<ReportConfig>) => void;
}

export function Step2Grouping({ config, updateConfig }: Step2GroupingProps) {
  return (
    <div className="space-y-6">
      {/* Group By */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Agrupar Dados Por</Label>
        <p className="text-sm text-muted-foreground">Como os dados devem ser organizados no relatório</p>

        <Select
          value={config.groupBy || 'none'}
          onValueChange={(value) => updateConfig({ groupBy: value === 'none' ? null : value as any })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Sem agrupamento" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sem agrupamento (resumo geral)</SelectItem>
            <SelectItem value="category">Categoria</SelectItem>
            <SelectItem value="cost_center">Centro de Custo</SelectItem>
            <SelectItem value="client">Cliente</SelectItem>
            <SelectItem value="supplier">Fornecedor</SelectItem>
            <SelectItem value="month">Mês</SelectItem>
            <SelectItem value="week">Semana</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Period Comparison */}
      <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label className="text-base font-semibold" htmlFor="comparison-toggle">
              Comparar com Período Anterior
            </Label>
            <p className="text-sm text-muted-foreground">
              Mostre a variação entre o período atual e o anterior
            </p>
          </div>
          <Switch
            id="comparison-toggle"
            checked={config.compareWith.enabled}
            onCheckedChange={(checked) =>
              updateConfig({
                compareWith: { ...config.compareWith, enabled: checked }
              })
            }
          />
        </div>

        {config.compareWith.enabled && (
          <div className="space-y-2">
            <Label>Tipo de Comparação</Label>
            <Select
              value={config.compareWith.type || 'previous_period'}
              onValueChange={(value) =>
                updateConfig({
                  compareWith: { ...config.compareWith, type: value as any }
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="previous_period">Período Anterior</SelectItem>
                <SelectItem value="same_period_last_year">Mesmo Período Ano Anterior</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Metric Type (when grouping is enabled) */}
      {config.groupBy && (
        <div className="space-y-3">
          <Label className="text-base font-semibold">Métrica de Agregação</Label>
          <p className="text-sm text-muted-foreground">Como calcular os valores para cada grupo</p>

          <Select
            value={config.metrics.amount}
            onValueChange={(value) =>
              updateConfig({
                metrics: { amount: value as any }
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sum">Soma</SelectItem>
              <SelectItem value="avg">Média</SelectItem>
              <SelectItem value="count">Contagem</SelectItem>
              <SelectItem value="min">Mínimo</SelectItem>
              <SelectItem value="max">Máximo</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
